import { ReportContentResultTypeEnum, reportContentResultTypes } from "../models/run.model";
import { CalculationResult, RegressionStats, RunStatus } from "../models/result.model";

/**
 * TrackBy function for Angular ngFor to optimize DOM rendering.
 *
 * Uses the lot number from the report content item as a unique identifier.
 *
 * @param index - Index of the current item in the ngFor loop.
 * @param item - The current item being iterated over.
 * @returns A unique identifier (lot) for tracking purposes.
 */
export function trackByFn(index: number, item: unknown): string {
  const reportContentItem = item as { reportContentItems: { lot: string } };
  return reportContentItem?.reportContentItems?.lot;
}

/**
 * Retrieves the result type name from an enum value.
 *
 * @param resultType - Enum value representing the result type.
 * @returns The human-readable name of the result type or an empty string if not found.
 */
export function getReportContentResultTypeName(resultType?: ReportContentResultTypeEnum): string {
  return reportContentResultTypes.find(type => type.resultTypeId === resultType)?.resultTypeName || '';
}

/**
 * Determines the color associated with a result string (PASS/FAIL/ALERT).
 *
 * Used for styling UI elements based on result status.
 *
 * @param result - The result string to evaluate.
 * @returns A CSS color code representing the result status.
 */
export function getStatusColorFromResult(result: string | undefined | null): string {
  switch (getResultStatus(result)) {
    case 'FAIL':
      return '#DC143C';
    case 'ALERT':
      return '#FF7F50';
    case 'PASS':
      return '#228B22';
    default:
      return 'black';
  }
}

/** PASS / ALERT / FAIL as read out of a free-text result string. */
export type ResultStatus = 'PASS' | 'ALERT' | 'FAIL' | 'NA';

export function getResultStatus(result: string | undefined | null): ResultStatus {
  if (!result) {
    return 'NA';
  }
  const upper = result.toUpperCase();
  if (upper.includes('FAIL')) {
    return 'FAIL';
  }
  if (upper.includes('ALERT')) {
    return 'ALERT';
  }
  if (upper.includes('PASS')) {
    return 'PASS';
  }
  return 'NA';
}

/**
 * The kit's status class for a result string — the class-based counterpart to
 * `getStatusColorFromResult`.
 *
 * Preferred over the hex version: an `[ngStyle]` color is unreachable from a stylesheet, so the
 * report tables could not restyle their statuses without editing every binding, and the three
 * hexes were separately hard-coded in five component stylesheets besides.
 */
export function getStatusClassFromResult(result: string | undefined | null): string {
  switch (getResultStatus(result)) {
    case 'FAIL':
      return 'bpx-status--fail';
    case 'ALERT':
      return 'bpx-status--alert';
    case 'PASS':
      return 'bpx-status--pass';
    default:
      return 'bpx-status--na';
  }
}

/**
 * Formats a summary string based on the count of pass/fail/alert results.
 *
 * @param pass - Number of PASS results.
 * @param fail - Number of FAIL results.
 * @param alert - Number of ALERT results.
 * @returns A string summarizing the result counts or an empty string if all are 0.
 */
export function runValidityPassFailAlertCount(pass: number, fail: number, alert: number) {
  if(fail === 0 && alert === 0 && pass === 0){
    return '';
  } else if(fail !== 0){
    return `FAIL: ${fail}`;
  } else if(alert !== 0){
    return `ALERT: ${alert}`;
  } else {
    return `PASS: ${pass}`;
  }
}

/**
 * Determines the overall status (PASS/FAIL/ALERT/NA) based on an array of individual results.
 *
 * Priority: FAIL > ALERT > PASS. If all values are null/undefined, returns NA.
 *
 * @param results - Array of result strings to evaluate.
 * @returns The overall result status as a string.
 */
export function resultPassFailAlert(results: string[] | undefined) {
  let overallStatus = "PASS";
  if (results?.some(result => result && result.toUpperCase() === 'FAIL')) {
    overallStatus = "FAIL";
  } else if (results?.some(result => result && result.toUpperCase() === 'ALERT')) {
    overallStatus = "ALERT";
  } else if (results?.every(val => val === null || val === undefined)) {
    overallStatus = "NA";
  }
  return overallStatus;
}

/**
 * Normalizes a status string to PASS/FAIL/ALERT, ignoring case and suffixes such as ": 2".
 * Values of '-', 'NA', or empty strings return null to indicate no actionable status.
 */
export function normalizeStatusValue(status: string | null | undefined): string | null {
  if (status === null || status === undefined) {
    return null;
  }
  const normalized = status.toString().trim().toUpperCase();
  if (!normalized || normalized === '-' || normalized === 'NA' || normalized === 'N/A') {
    return null;
  }
  if (normalized.includes('FAIL')) {
    return 'FAIL';
  }
  if (normalized.includes('ALERT')) {
    return 'ALERT';
  }
  if (normalized.includes('PASS')) {
    return 'PASS';
  }
  return null;
}

/**
 * Derives the overall Run Validity status (PASS/FAIL/ALERT/NA) from the provided calculation results.
 * Evaluates all cells rendered in the Run Validity section (kit status, patient specs, curve validity,
 * control statuses, and the negative/cut-off/positive sample summaries).
 */
export function deriveRunValidityStatus(
  original?: CalculationResult | null,
  adjusted?: CalculationResult | null
): string {
  const statuses: string[] = [];
  const appendStatuses = (result?: CalculationResult | null) => {
    if (!result) {
      return;
    }

    const regressionMap: Map<string, RegressionStats> = new Map();
    result.regressionStats?.forEach(stat => regressionMap.set(stat.reportContentItemId, stat));

    result.runStatus?.forEach(runStatus => {
      collectRunValidityStatuses(statuses, runStatus);
      const regression = regressionMap.get(runStatus.reportContentItemId);
      if (regression) {
        collectRunValiditySampleStatuses(statuses, regression);
      }
    });
  };

  appendStatuses(original ?? null);
  appendStatuses(adjusted ?? null);

  return resultPassFailAlert(statuses);
}

function collectRunValidityStatuses(target: string[], runStatus: RunStatus | undefined): void {
  if (!runStatus) {
    return;
  }
  addNormalizedStatus(target, runStatus.kitStatus);
  addNormalizedStatus(target, runStatus.calibratorSpecStatus);
  addNormalizedStatus(target, runStatus.patientSampleSpecStatus);
  addNormalizedStatus(target, runStatus.patientSampleAcceptanceRange);
  addNormalizedStatus(target, runStatus.controlResultsValidity);
  addNormalizedStatus(target, runStatus.sampleReplicateCountValidity);
  addNormalizedStatus(target, runStatus.sampleReplicateCVValidity);
  addNormalizedStatus(target, runStatus.controlTestStatus);

  const curveInfo = runStatus.calCurveInformation;
  if (curveInfo) {
    addNormalizedStatus(target, curveInfo.calCurveValidity);
    addNormalizedStatus(target, curveInfo.calReplicateValidity);
  }
}

function collectRunValiditySampleStatuses(target: string[], regression: RegressionStats): void {
  addNormalizedStatus(
    target,
    runValidityPassFailAlertCount(
      regression.negativeSamplesPASS || 0,
      regression.negativeSamplesFAILED || 0,
      regression.negativeSamplesALERT || 0
    )
  );
  addNormalizedStatus(
    target,
    runValidityPassFailAlertCount(
      regression.cutOffSamplesPASS || 0,
      regression.cutOffSamplesFail || 0,
      regression.CutOffSamplesAlert || 0
    )
  );
  addNormalizedStatus(
    target,
    runValidityPassFailAlertCount(
      regression.positiveSamplesPASS || 0,
      regression.positiveSamplesFAILED || 0,
      regression.positiveSamplesAlert || 0
    )
  );
}

function addNormalizedStatus(target: string[], status: string | null | undefined): void {
  const normalized = normalizeStatusValue(status);
  if (normalized) {
    target.push(normalized);
  }
}
